// Single source of truth for the /apply multi-step form.
// Options must stay in lockstep with /app/backend/models/application.py.

export const APPLY_STAGES = [
  { id: 1, name: "Basic Details" },
  { id: 2, name: "Professional Details" },
  { id: 3, name: "Membership Summary" },
];

// Simplified — "Chapter" is already in the question label.
export const CITY_CHAPTERS = [
  "Bangalore",
  "Mumbai",
  "Delhi NCR",
  "Hyderabad",
  "Pune",
  "Raipur",
];

export const GENDERS = ["Male", "Female"];

export const PROFESSIONAL_TYPES = [
  "Working Professional",
  "Freelancer",
  "Business Owner / Founder",
  "Other",
];

// The 9 core Professional Circles — used for both primary and secondary.
export const PROFESSIONAL_CIRCLES = [
  "Finance, Legal & Compliance",
  "Marketing & Content",
  "Tech, Software & AI",
  "Real Estate, Architecture & Interiors",
  "Education, Training & Coaching",
  "Fitness & Wellness",
  "Healthcare & Medical",
  "Events and Experiences",
  "Visual & Creative Arts",
];

// Primary circle — 9 circles + final "None of the listed…" option.
export const PRIMARY_CIRCLE_NONE =
  "None of the listed Professional Circles – Join City Members Group only where members from all circles from my city chapter are already present.";

export const PRIMARY_CIRCLE_OPTIONS = [
  ...PROFESSIONAL_CIRCLES,
  PRIMARY_CIRCLE_NONE,
];

// Secondary circle — 9 circles + "Not Applicable".
export const SECONDARY_CIRCLE_NA = "Not Applicable";

export const SECONDARY_CIRCLE_OPTIONS = [
  ...PROFESSIONAL_CIRCLES,
  SECONDARY_CIRCLE_NA,
];

export const EXPERIENCE_RANGES = [
  "Less than 1 year",
  "1–3 years",
  "3–5 years",
  "5–10 years",
  "10+ years",
];

export const LOOKING_FOR_OPTIONS = [
  "New opportunities & clients",
  "Team members / hiring",
  "Collaborations & partnerships",
  "Mentorship & guidance",
  "Learning & skill sharing",
  "Meeting like-minded professionals",
  "Investment & funding conversations",
];

export const MEMBERSHIP_BENEFITS = [
  "Access to a curated professional community",
  "Access to Professional Circles relevant to your work profile",
  "Access to your City Chapter\u2019s cross-industry professional network",
  "Access to member-exclusive online networking meetups",
  "Eligibility to join member-exclusive offline networking meetups",
];

// Four mandatory acknowledgements shown just above the Submit button.
export const ACKNOWLEDGEMENTS = [
  {
    key: "accuracy",
    label:
      "I confirm that the information provided is accurate to the best of my knowledge.",
  },
  {
    key: "misuse",
    label:
      "I understand that spam, irrelevant messages, or misuse of the network may lead to cancellation of my membership and removal from the community.",
  },
  {
    key: "non_refundable",
    label:
      "I understand that the membership fee is non-refundable after my membership is approved.",
  },
  {
    key: "whatsapp_registered",
    label:
      "I understand that my membership is valid only for my registered WhatsApp number, which will be visible to other members in the WhatsApp community.",
  },
];

// Empty draft shape — mirrors backend ApplicationBase.
export const EMPTY_DRAFT = {
  city_chapter: "",
  full_name: "",
  gender: "",
  date_of_birth: "", // DD/MM/YYYY
  whatsapp_number: "",
  current_city: "",
  email: "",
  linkedin_url: "",
  instagram_username: "",
  professional_type: "",
  professional_type_other: "",
  primary_circle: "",
  professional_description: "",
  experience_range: "",
  secondary_circle: "",
  secondary_description: "",
  looking_for: [],
  notes: "",
  acknowledgements: {
    accuracy: false,
    misuse: false,
    non_refundable: false,
    whatsapp_registered: false,
  },
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const DOB_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** Returns true only for a real calendar date in DD/MM/YYYY format. */
export function isValidDob(v) {
  if (!v || typeof v !== "string") return false;
  const m = v.match(DOB_RE);
  if (!m) return false;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;
  if (yyyy < 1900 || yyyy > new Date().getFullYear()) return false;
  const d = new Date(yyyy, mm - 1, dd);
  if (
    d.getFullYear() !== yyyy ||
    d.getMonth() !== mm - 1 ||
    d.getDate() !== dd
  )
    return false;
  return true;
}

/** Formats raw digits into DD/MM/YYYY as the user types. */
export function formatDobInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function validateStage1(v) {
  const errors = {};
  if (!v.city_chapter || !CITY_CHAPTERS.includes(v.city_chapter))
    errors.city_chapter = "Please select your City Chapter.";
  if (!v.full_name || !v.full_name.trim())
    errors.full_name = "Full Name is required.";
  if (!v.gender || !GENDERS.includes(v.gender))
    errors.gender = "Please select your Gender.";
  if (!v.date_of_birth || !isValidDob(v.date_of_birth))
    errors.date_of_birth = "Enter a valid date of birth in DD/MM/YYYY format.";
  // WhatsApp / mobile — Indian numbers only. Must be exactly 10 digits
  // and start with 6-9 (the valid prefix range for Indian mobile
  // operators per TRAI's numbering plan). Same rule enforced server-side
  // in applications._validate_submission.
  const waDigits = (v.whatsapp_number || "").replace(/\D/g, "");
  if (!/^[6-9]\d{9}$/.test(waDigits))
    errors.whatsapp_number = "Enter a valid 10-digit Indian mobile number (starting with 6, 7, 8, or 9).";
  if (!v.current_city || !v.current_city.trim())
    errors.current_city = "Current City is required.";
  if (!v.email || !EMAIL_RE.test(v.email))
    errors.email = "Enter a valid email address.";
  return errors;
}

export function validateStage2(v) {
  const errors = {};
  if (!v.professional_type || !PROFESSIONAL_TYPES.includes(v.professional_type))
    errors.professional_type = "Please choose the option that best describes you.";
  if (
    v.professional_type === "Other" &&
    (!v.professional_type_other || !v.professional_type_other.trim())
  )
    errors.professional_type_other = "Please specify.";
  if (!v.primary_circle || !PRIMARY_CIRCLE_OPTIONS.includes(v.primary_circle))
    errors.primary_circle = "Please choose your Primary Professional Circle.";
  if (!v.professional_description || v.professional_description.trim().length < 3)
    errors.professional_description = "Please describe what you do professionally.";
  if (!v.experience_range || !EXPERIENCE_RANGES.includes(v.experience_range))
    errors.experience_range = "Please select your years of experience.";
  if (!v.secondary_circle || !SECONDARY_CIRCLE_OPTIONS.includes(v.secondary_circle))
    errors.secondary_circle = "Please choose a Secondary Professional Circle (or Not Applicable).";
  if (!v.secondary_description || !v.secondary_description.trim())
    errors.secondary_description = 'Please describe your work in this circle (write "NA" if not applicable).';
  if (!Array.isArray(v.looking_for) || v.looking_for.length === 0)
    errors.looking_for = "Please select at least one option.";
  return errors;
}

export function validateAcknowledgements(a) {
  const errors = {};
  const ack = a || {};
  const missing = ACKNOWLEDGEMENTS.filter((it) => !ack[it.key]);
  if (missing.length > 0) errors.acknowledgements = "Please tick all the acknowledgements to submit.";
  return errors;
}
